import React, { useState } from 'react';
import {
  Chart as ChartJS,
  Tooltip,
  CategoryScale,
  LinearScale,
  PointElement,
} from 'chart.js';
import { Bubble } from 'react-chartjs-2';
// import { Background } from 'reactflow';

ChartJS.register(Tooltip, CategoryScale, LinearScale, PointElement);

export default function TaskQueueMatrixChart({
  data,
  onSelectQueue,
  queuesWithoutWorkers = [],
}) {
  const rawWorkflowLabels = Array.from(new Set(data.map(d => d.workflowName)));

  const [selectedQueueSid, setSelectedQueueSid] = useState(null);


  // Add empty strings to left and right for spacing
  const workflowLabels = [''].concat(rawWorkflowLabels).concat(['']);

  const queuesWithoutWorkersSet = new Set(queuesWithoutWorkers);

  const yLabels = [];
  for (let i = 0; i <= 10; i++) {
    yLabels[10 - i] = i.toString();
  }
  yLabels[11] = 'Overflow 1';
  yLabels[12] = 'Overflow 2';
  yLabels[13] = 'Overflow 3';

  const maxY = 13;

  const bubbleData = data.map(item => {
    const noWorkers = queuesWithoutWorkersSet.has(item.queueSid);
    let yPos;

    if (item.targetIndex >= 1 && item.targetIndex <= 3) {
      yPos = 11 + (item.targetIndex - 1);
    } else {
      yPos = item.priority != null ? 10 - item.priority : 10;
    }

    const isSelected = item.queueSid === selectedQueueSid;

    return {
      x: workflowLabels.indexOf(item.workflowName),
      y: yPos,
      r: isSelected ? 70 : 50,
      backgroundColor: isSelected
        ? 'rgba(255, 206, 86, 1)' // bright yellow
        : noWorkers
        ? 'rgba(255, 99, 132, 0.9)'
        : 'rgba(54, 162, 235, 0.9)',
      borderColor: isSelected ? 'black' : noWorkers ? 'darkred' : 'transparent',
      borderWidth: isSelected ? 1 : noWorkers ? 1 : 0,
      queueSid: item.queueSid,
      queueName: item.queueName,
      priority: item.priority,
      targetIndex: item.targetIndex,
    };
  });


  const chartData = {
    datasets: [
      {
        label: '',
        data: bubbleData,
        backgroundColor: bubbleData.map(d => d.backgroundColor),
        borderColor: bubbleData.map(d => d.borderColor),
        borderWidth: bubbleData.map(d => d.borderWidth),
        parsing: false,
      },
    ],
  };

  const overflowRowBackgroundPlugin = {
    id: 'overflowRowBackgroundPlugin',
    beforeDraw: chart => {
      const {
        ctx,
        chartArea: { left, right, top, bottom },
        scales: { x, y },
      } = chart;

      const drawRow = (rowIndex, color) => {
        const centerY = y.getPixelForValue(rowIndex);
        const nextY = y.getPixelForValue(rowIndex + 1) ?? y.getPixelForValue(rowIndex);
        const height = Math.abs(centerY - nextY);

        ctx.save();
        ctx.fillStyle = color;
        ctx.fillRect(left, centerY - height / 2, right - left, height);
        ctx.restore();
      };

      // Draw overflow rows
      [11, 12, 13].forEach(row => {
        drawRow(row, 'rgba(179, 138, 138, 0.3)');
      });

      // Draw every second vertical line with a darker color for differentiation
      const xLabels = x.getLabels();
      for (let i = 0; i < xLabels.length; i++) {
        if (i % 2 === 1 && i !== 0 && i !== xLabels.length - 1) {
          const xPos = x.getPixelForValue(xLabels[i]);
          ctx.save();
          ctx.strokeStyle = '#2d4a7a81'; // true dark color
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(xPos, top);
          ctx.lineTo(xPos, bottom);
          ctx.stroke();
          ctx.restore();
        } else {
          ctx.strokeStyle = '#cbb0b07e'; // lighter color
          ctx.lineWidth = 1;
          ctx.beginPath();
          const xPos = x.getPixelForValue(xLabels[i]);
          ctx.moveTo(xPos, top);
          ctx.lineTo(xPos, bottom);
          ctx.stroke();
          ctx.restore();
        }
      }
    },
  };

  const chartOptions = {
    scales: {
      x: {
        type: 'category',
        labels: workflowLabels,
        title: { 
          text: 'Workflow Names', 
          color: '#2d4a7a81',
          padding: { top: 20 },
          font: {
            size: 13,
            weight: 'bold',
          },
        },
        ticks: {
          color: (context) => {
            const idx = context.index;
            // Alternate color for each label to match vertical grid line pattern
            return idx % 2 === 0 ? '#cbb0b0ff' : '#2d4a7a';
          },
          font: { size: 10, family: 'Wix Madefor Text, sans-serif' },
          maxRotation: 60,
          minRotation: 45,
          callback: (val, index) => {
            const label = workflowLabels[index];
            return label || '';
          },
        },
      },
      y: {
        type: 'category',
        labels: yLabels,
        min: 0,
        max: maxY,
        reverse: true,
        title: { 
          text: 'Priority & Overflow Levels', 
          color: '#2d4a7a7b',
          padding: { bottom: 20 },
          font: {
            size: 13,
            weight: 'bold',
          },
        },
        ticks: {
          color: '#2d4a7a',
          font: { size: 13, family: 'Wix Madefor Text, sans-serif' },
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
        },
      },
    },
    plugins: {
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: ctx => {
            const { queueName, priority, targetIndex } = ctx.raw || {};
            const indexText =
              targetIndex >= 1 ? `Target Overflow: ${targetIndex}` : '';
            return `Queue: ${queueName} (Priority ${priority ?? 'N/A'})${
              indexText ? '\n' + indexText : ''
            }`;
          },
        },
      },
      legend: { display: false },
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const { datasetIndex, index } = elements[0];
        const dataset = chartData.datasets[datasetIndex];
        const dataPoint = dataset.data[index];
        if (dataPoint?.queueSid) {
          setSelectedQueueSid(dataPoint.queueSid);
          onSelectQueue?.(dataPoint.queueSid);
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <div
      style={{
        height: '500px',
        minWidth: '1200px',
        overflowX: 'auto',
        background: 'linear-gradient(135deg, #f7fafd 0%, #fff 100%)',
        borderRadius: 18,
        boxShadow: '0 4px 24px rgba(14,66,106,0.08)',
        border: '1.5px solid #e3eaf3',
        padding: '1.5rem 1.2rem',
        marginBottom: '1.5rem',
        position: 'relative',
        transition: 'box-shadow 0.2s',
      }}
    >
      <Bubble
        data={chartData}
        options={chartOptions}
        plugins={[overflowRowBackgroundPlugin]}
      />
    </div>
  );
}
